import { Close } from '@mui/icons-material';
import {
	Box,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogProps,
	DialogTitle,
	IconButton,
	MenuItem
} from '@mui/material';
import Modal from '../modal/modal';
import Typography from '../typography/typography';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from '../select/select';
import { Controller, useForm } from 'react-hook-form';

import './quick-approval-modal.scss';
import { useEffect, useState } from 'react';
import Avatar from '../avatar/avatar';
import { formatLabel } from '../../utils/format-text';
import Checkbox from '../checkbox/checkbox';
import Button from '../button/button';
import ChipOrPlaceholder from '../form-control/form-builder/form-builder-element/chip-or-placeholder';
import Chip from '../chip/chip';
import { MoreIcon } from '../icons';
import { getV1User } from '../../api-client/api.rbac/api';
import { enqueueSnackbar } from 'notistack';

import React from 'react';
import { getToken } from '../../utils/common';

interface QuickApprovalModalProps {
	isOpen: boolean;
	setIsOpen: () => void;
	modalProps?: DialogProps;
	title?: string;
	onConfirm: (approverIds: number[]) => void;
  isProcessing?: boolean
}

interface IFormInput {
	approver_id: number[];
}
interface IUsersData {
	isLoading: boolean
	isError: boolean
  error: string | null
  data: Array<Record<string, any>>
}

const validationSchema = Yup.object().shape({
	approver_id: Yup.array()
		.required('Please select the approvers')
		.min(1, 'Please select the approvers')
});

const QuickApprovalModal: React.FC<QuickApprovalModalProps> = ({
	isOpen,
	setIsOpen,
	modalProps,
	title,
	onConfirm,
  isProcessing
}) => {
	const [users, setUsers] = useState<IUsersData>({
		isLoading: true,
		isError: false,
		error: 'null',
		data: []
	});
	const { isLoading, isError, error, data } = users;

	const { control, handleSubmit, reset, setValue, getValues, watch, trigger } =
		useForm<IFormInput>({
			resolver: yupResolver(validationSchema),
			mode: 'all'
		});

	watch('approver_id');
	const formValues = getValues('approver_id');

	const onSubmit = async (values: IFormInput) => {
		const isValid = await trigger();
		if (!isValid) return;
		onConfirm(values.approver_id);
		handleModalClose();
	};

	const handleModalClose = () => {
		reset({approver_id: []});
		setIsOpen();
	};

	const SelectedItems = ({
		data,
		options
	}: {
		data: number[];
		options: any[];
	}) => {
		const displayTitle = data.map((selectedItem) => {
			const user = options.find((option: any) => option.id === selectedItem);
			if (user) {
				return getLabel(user);
			}
			return selectedItem;
		});

		return (
			<Box display='flex' justifyContent='space-between' alignItems='center'>
				<Typography
					type='s5'
					color='inherit'
					sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
					{displayTitle.slice(0, 3).join(', ')}
					{data.length > 4 && (
						<Chip
							variant='outlined'
							type='normal'
							className='quickApprovalModal--DisplayNameChip'
							label={<MoreIcon fontSize='small' />}
						/>
					)}
				</Typography>
				<Button variant='text' onClick={() => setValue('approver_id', [])}>
					<Typography type='s5' color='inherit'>
						Remove
					</Typography>
				</Button>
			</Box>
		);
	};

	const getAndSetFetchUsers = async () => {
		try {
			const filters = { skip: 0, limit: 99, filters: `(is_active.eq=true)` };
			const response = await getV1User({ ...filters, ...getToken() });
	
			const users: any[] = response?.data?.users || [];
			setUsers({
				isLoading: false,
				isError: false,
				error: null,
				data: users
			});	
		} catch (error) {
			setUsers({
				isLoading: false,
				isError: true,
				error: 'Failed to fetch Approvers',
				data: []
			});
		}
	};

	useEffect(() => {
		if (isOpen) {
			getAndSetFetchUsers()
		}
	}, [isOpen]);
	useEffect(() => {
		if (isError) {
			enqueueSnackbar(error ?? 'Something went wrong', { variant: 'error'});
		}
	}, [error, isError]);

	const getLabel = (obj: Record<any, any>) => {
		const {first_name = '', last_name=''} = obj || {}

		return formatLabel(`${first_name} ${last_name}`)
	}

  useEffect(() => {
    return () => {
      handleModalClose()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

	return (
		<Modal
			open={isOpen}
			onClose={handleModalClose}
			maxWidth='sm'
			fullWidth
			disableEscapeKeyDown
			{...modalProps}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<DialogTitle className='quickApprovalModal--Title'>
					<Typography type='s3' color='inherit' weight='medium'>
						{title || 'Quick Approval'}
					</Typography>
					<IconButton onClick={handleModalClose}>
						<Close fontSize='small' />
					</IconButton>
				</DialogTitle>
				<DialogContent className='quickApprovalModal--ModalContentContainer'>
					<Controller
						name='approver_id'
						control={control}
						render={({ field, fieldState }) => (
							<>
								<Select
									name='approver_id'
									value={field.value || []}
									onChange={field.onChange}
									error={Boolean(fieldState.error?.message)}
									helperText={fieldState.error?.message || ''}
									size='small'
									renderValue={(selected: string[]) => (
										<ChipOrPlaceholder
											data={selected}
											placeholder='Select'
											onDelete={() => setValue('approver_id', [])}
										/>
									)}
									multiple
									fullWidth>
									{data.map((user) => {
										const colors = [
											'theme.tertiary1',
											'theme.tertiary2',
											'theme.tertiary3'
										];
										const randomIndex = Math.floor(
											Math.random() * colors.length
										);
										const randomColor = colors[randomIndex];
										const avatarStyle = {
											borderRadius: '0.25rem !important',
											textAlign: 'center',
											backgroundColor: `${randomColor}.200`,
											color: `${randomColor}.1000`
										};

										return (
											<MenuItem
												key={user.id}
												value={user.id}
												selected={Boolean(
													field.value && field.value.includes(user.id)
												)}>
												<Box className='quickApprovalModal--UserListingContainer'>
													<Box className='quickApprovalModal--UserContent'>
														<Avatar
															src={user.profile_image}
															alt={getLabel(user)}
															sx={avatarStyle}
															size={24}
														/>
														<Typography type='s4' color='theme.secondary.1000'>
															{getLabel(user)}
														</Typography>
													</Box>
													<Checkbox
														checked={Boolean(
															field.value && field.value.includes(user.id)
														)}
													/>
												</Box>
											</MenuItem>
										);
									})}
								</Select>
								{Array.isArray(field?.value) && field?.value?.length > 0 && (
									<SelectedItems data={field.value} options={data} />
								)}
							</>
						)}
					/>
				</DialogContent>
				<DialogActions className='quickApprovalModal--DialogActions'>
					<Button
						className='quickApprovalModal--DialogActions--Cancel'
						onClick={handleModalClose}
						disabled={isLoading || isProcessing}>
						Cancel
					</Button>
					<Button
						type='button'
						onClick={handleSubmit(onSubmit)}
						endIcon={
							isProcessing ? (
								<CircularProgress size={15} color='inherit' />
							) : undefined
						}
						disabled={isProcessing || isLoading || Boolean(!formValues?.length)}>
						Send Request
					</Button>
				</DialogActions>
			</form>
		</Modal>
	);
};

export default QuickApprovalModal;